import { formatDateTime } from '@if/core/utils'
import { Caption, EnumChip, Icon, MetaValue, useDialog } from '@if/mui'
import { Box, Divider, Stack } from '@mui/material'
import { ELoanStatus } from '@src/types/constants'
import { LoanStatusHistory } from '@src/types/loan/loan-calendar'
import { ArrowRight, History } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ELoanStatusSet, toLoanStatusValue } from '../loan-status'

interface LoanStatusHistoryDialogProps {
  loanStatusHistory: LoanStatusHistory[]
  currentLoanStatus: ELoanStatus | null
}

interface StageTransition {
  entry: LoanStatusHistory
  previousStage: ELoanStatus
}

const toStageTransitions = (loanStatusHistory: LoanStatusHistory[]): StageTransition[] => {
  const entriesOldestFirst = [...loanStatusHistory].sort((left, right) => left.id - right.id)
  const transitions: StageTransition[] = []
  entriesOldestFirst.forEach((entry, index) => {
    const previous = entriesOldestFirst[index - 1]
    if (previous !== undefined && previous.loanStatusId !== entry.loanStatusId) {
      transitions.push({ entry, previousStage: previous.loanStatusId })
    }
  })
  return transitions.reverse()
}

export const useLoanCalendarLoanStatusHistoryDialog = () => {
  const { t } = useTranslation()

  return useDialog<LoanStatusHistoryDialogProps>({
    icon: History,
    title: t('loan.payment.loanStatusHistoryDialog.title'),
    edgeToEdge: true,
    hideConfirm: true,
    maxWidth: 'md',
    fullWidth: true,
    toolbar: ({ props }) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Caption>
          {t('loan.payment.loanStatusHistoryDialog.count', {
            count: toStageTransitions(props.loanStatusHistory).length,
          })}
        </Caption>
        {props.currentLoanStatus !== null && (
          <EnumChip size="small" set={ELoanStatusSet} value={toLoanStatusValue(props.currentLoanStatus)} />
        )}
      </Stack>
    ),
    content: ({ props }) => {
      const transitions = toStageTransitions(props.loanStatusHistory)
      if (transitions.length === 0) {
        return (
          <Caption sx={{ display: 'block', px: 3, py: 4, textAlign: 'center' }}>
            {t('loan.payment.loanStatusHistoryDialog.empty')}
          </Caption>
        )
      }
      return (
        <Box>
          {transitions.map(({ entry, previousStage }, index) => (
            <Box key={entry.id}>
              {index > 0 && <Divider />}
              <Stack direction="row" spacing={2} sx={{ px: 3, py: 1.5, alignItems: 'center' }}>
                <MetaValue>{formatDateTime(entry.updatedOn)}</MetaValue>
                <Caption sx={{ flex: 1 }}>{entry.updatedByFullName ?? '—'}</Caption>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <EnumChip size="small" set={ELoanStatusSet} value={toLoanStatusValue(previousStage)} />
                  <Icon icon={ArrowRight} size="sm" color="action" />
                  <EnumChip size="small" set={ELoanStatusSet} value={toLoanStatusValue(entry.loanStatusId)} />
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      )
    },
  })
}
